import { Controller } from "@hotwired/stimulus";
import { enqueueHarvestEntry } from "../../db/harvestEntries.js";
import { checkOnlineStatus } from "../../onlineStatus.js";
import { farmDatetimeLocalNow } from "../../helpers/farm_time.js";
import * as Turbo from "@hotwired/turbo";

// Connects to data-controller="harvest-entries--form"
export default class extends Controller {
  static targets = [
    "emptyVehicleWeightValue",
    "emptyVehicleWeightUnit",
    "harvestUnitSelect",
    "harvestUnitsLabel",
    "entryAt",
  ];
  static values = {
    farmId: Number,
    timeZone: String,
    indexUrl: String,
    errorRequired: String,
    errorGreaterThanZero: String,
    errorWeightExceedsScale: String,
    harvestUnitsDefaultLabel: String,
  };

  connect() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.element.addEventListener("submit", (e) => this.#handleSubmit(e), {
      signal,
    });
    this.element.addEventListener(
      "change",
      (e) => this.#handleOfflineFieldChange(e),
      { signal }
    );

    this.updateHarvestUnitLabel();
    this.#defaultEntryAtToNow();
    this.#syncEmptyVehicleWeightFields();
  }

  disconnect() {
    this.#abortController.abort();
  }

  updateHarvestUnitLabel() {
    const selectedOption = this.harvestUnitSelectTarget.selectedOptions[0];
    this.harvestUnitsLabelTarget.textContent =
      selectedOption?.value ? selectedOption.text : this.harvestUnitsDefaultLabelValue;
  }

  #abortController;
  #submittingOnline = false;

  #defaultEntryAtToNow() {
    if (!this.hasEntryAtTarget || this.entryAtTarget.value) return;

    this.entryAtTarget.value = farmDatetimeLocalNow(this.timeZoneValue);
  }

  #syncEmptyVehicleWeightFields() {
    const vehicleId = this.element.querySelector(
      '[name="harvest_entry[vehicle_id]"]'
    )?.value;
    const entryAt = this.element.querySelector(
      '[name="harvest_entry[entry_at]"]'
    )?.value;

    if (!vehicleId || !entryAt) return;

    if (this.hasEmptyVehicleWeightValueTarget) {
      this.emptyVehicleWeightValueTarget.removeAttribute("disabled");
    }
    if (this.hasEmptyVehicleWeightUnitTarget) {
      this.emptyVehicleWeightUnitTarget.removeAttribute("disabled");
    }
  }

  async #handleSubmit(event) {
    if (this.#submittingOnline) return;
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "commit") return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (await checkOnlineStatus()) {
      this.#submittingOnline = true;
      this.element.requestSubmit(submitter);
      return;
    }

    this.#clearErrors();
    const errors = this.#validate();

    if (errors.length > 0) {
      this.#showErrors(errors);
      return;
    }

    this.#saveOffline(submitter.value);
  }

  #validate() {
    const errors = [];
    const val = (name) =>
      this.element
        .querySelector(`[name="harvest_entry[${name}]"]`)
        ?.value?.trim();

    if (!val("entry_at"))
      errors.push({ field: "entry_at", message: this.errorRequiredValue });
    if (!val("vehicle_id"))
      errors.push({ field: "vehicle_id", message: this.errorRequiredValue });
    if (!val("lot_id"))
      errors.push({ field: "lot_id", message: this.errorRequiredValue });
    if (!val("harvest_group_id"))
      errors.push({
        field: "harvest_group_id",
        message: this.errorRequiredValue,
      });
    if (!val("harvest_unit_id"))
      errors.push({
        field: "harvest_unit_id",
        message: this.errorRequiredValue,
      });

    const harvestUnits = parseInt(val("harvest_units"));
    if (!harvestUnits || harvestUnits <= 0) {
      errors.push({
        field: "harvest_units",
        message: this.errorGreaterThanZeroValue,
      });
    }

    const scaleWeight = parseFloat(val("scale_weight_value"));
    if (!scaleWeight || scaleWeight <= 0) {
      errors.push({
        field: "scale_weight_value",
        message: this.errorGreaterThanZeroValue,
      });
    }

    const emptyWeight = parseFloat(val("empty_vehicle_weight_value"));
    if (!emptyWeight && emptyWeight !== 0) {
      errors.push({
        field: "empty_vehicle_weight_value",
        message: this.errorRequiredValue,
      });
    } else if (scaleWeight > 0 && emptyWeight > scaleWeight) {
      errors.push({
        field: "empty_vehicle_weight_value",
        message: this.errorWeightExceedsScaleValue,
      });
    }

    return errors;
  }

  #showErrors(errors) {
    const targets = errors.map(({ field, message }) => {
      const input = this.element.querySelector(
        `[name="harvest_entry[${field}]"]`
      );
      const wrapper = input?.closest(".fieldset");
      return { input, wrapper, message };
    });

    for (const { input, wrapper, message } of targets) {
      if (!input || !wrapper) continue;
      const errorEl = document.createElement("p");
      errorEl.className = "text-sm text-error mt-1 js-offline-error";
      errorEl.textContent = message;
      wrapper.appendChild(errorEl);
      input.classList.add("input-error");
    }
  }

  #clearErrors() {
    this.element
      .querySelectorAll(".js-offline-error")
      .forEach((el) => el.remove());
    this.element
      .querySelectorAll(".input-error")
      .forEach((el) => el.classList.remove("input-error"));
  }

  async #saveOffline(commitValue) {
    const formData = new FormData(this.element);
    const payload = {};
    for (const [key, value] of formData.entries()) {
      payload[key] = value;
    }
    payload["commit"] = commitValue;

    const display = this.#extractDisplayData();

    try {
      await enqueueHarvestEntry(this.farmIdValue, payload, display);
    } catch (err) {
      console.error("[harvest-entries--form] Failed to save offline:", err);
      return;
    }

    sessionStorage.setItem("harvestEntryOfflineFlash", "1");
    Turbo.visit(this.indexUrlValue);
  }

  #extractDisplayData() {
    const elements = {};
    const fields = [
      "entry_at", "vehicle_id", "lot_id", "harvest_group_id", "harvest_unit_id",
      "harvest_units", "scale_weight_value", "scale_weight_unit",
      "empty_vehicle_weight_value", "empty_vehicle_weight_unit",
      "scale_ticket_code", "field_statement_code",
    ];
    for (const name of fields) {
      elements[name] = this.element.querySelector(
        `[name="harvest_entry[${name}]"]`
      );
    }

    const val = (name) => elements[name]?.value?.trim() || "";
    const selectText = (name) =>
      elements[name]?.selectedOptions?.[0]?.text || "";

    const scaleVal = parseFloat(val("scale_weight_value")) || 0;
    const emptyVal = parseFloat(val("empty_vehicle_weight_value")) || 0;
    const netVal = scaleVal - emptyVal;
    const units = parseInt(val("harvest_units")) || 1;
    const avgVal = units > 0 ? netVal / units : 0;
    const entryAt = val("entry_at");

    return {
      entry_at: entryAt ? new Date(entryAt).toLocaleDateString(document.documentElement.lang || undefined) : "",
      vehicle_name: selectText("vehicle_id"),
      lot_name: selectText("lot_id"),
      harvest_group_name: selectText("harvest_group_id"),
      harvest_unit_name: selectText("harvest_unit_id"),
      scale_weight: `${scaleVal} ${val("scale_weight_unit")}`,
      empty_vehicle_weight: `${emptyVal} ${val("empty_vehicle_weight_unit")}`,
      net_weight: `${netVal.toFixed(2)} ${val("scale_weight_unit")}`,
      average_harvest_unit_weight: `${avgVal.toFixed(2)} ${val("scale_weight_unit")}`,
      harvest_units: val("harvest_units"),
      scale_ticket_code: val("scale_ticket_code"),
      field_statement_code: val("field_statement_code"),
    };
  }

  async #handleOfflineFieldChange(event) {
    if (await checkOnlineStatus()) return;

    const name = event.target.name;
    if (
      name !== "harvest_entry[vehicle_id]" &&
      name !== "harvest_entry[entry_at]"
    )
      return;

    const vehicleId = this.element.querySelector(
      '[name="harvest_entry[vehicle_id]"]'
    )?.value;
    const entryAt = this.element.querySelector(
      '[name="harvest_entry[entry_at]"]'
    )?.value;

    this.#syncEmptyVehicleWeightFields();
  }
}
